import useIsNativeApp from '@hooks/useIsNativeApp'
import {
  findServiceWorkerForBuild,
  getCurrentBuildId,
  getNativeOfflineRequiredRoutes,
  getServiceWorkerBuildId,
  isNativeOfflineReadyForCurrentBuild,
  markNativeOfflineReady,
  NATIVE_OFFLINE_READY_MESSAGE_TYPE,
  NATIVE_OFFLINE_SKIP_WAITING_MESSAGE_TYPE,
  verifyNativeOfflineCacheManifest,
  verifyWarmedRouteShells,
  writeNativeOfflineReadinessManifest,
  warmRouteShells,
} from 'helpers/nativeOfflineBootstrap'
import { useEffect, useRef } from 'react'

const ROUTE_BATCH_SIZE = 8
const RETRY_DELAY_MS = 15_000

/** Splits the deterministic route list into retryable network batches. */
const splitIntoBatches = <T,>(items: T[], batchSize: number): T[][] => {
  const batches: T[][] = []
  for (let index = 0; index < items.length; index += batchSize) {
    batches.push(items.slice(index, index + batchSize))
  }
  return batches
}

type NativeOfflinePreparationResult = 'retry' | 'waiting' | 'ready'

/** Posts the cleanup command only after the matching worker controls the page. */
const commitActiveNativeOfflineBuild = async (
  registration: ServiceWorkerRegistration,
  buildId: string,
): Promise<boolean> => {
  const activeWorker = registration.active
  if (!activeWorker) return false
  if ((await getServiceWorkerBuildId(activeWorker)) !== buildId) return false
  if (!(await verifyNativeOfflineCacheManifest(buildId))) return false

  if (!markNativeOfflineReady(buildId)) return false

  try {
    activeWorker.postMessage({
      type: NATIVE_OFFLINE_READY_MESSAGE_TYPE,
      buildId,
    })
  } catch {
    return true
  }

  return true
}

/** Prepares and verifies the current web shell before activating a new worker. */
const prepareNativeOfflineShell = async (): Promise<{
  buildId: string | null
  result: NativeOfflinePreparationResult
}> => {
  if (
    typeof window === 'undefined' ||
    !('serviceWorker' in navigator) ||
    !navigator.onLine
  ) {
    return { buildId: null, result: 'retry' }
  }

  const buildId = getCurrentBuildId()
  if (!buildId) return { buildId: null, result: 'retry' }

  const registration = await navigator.serviceWorker.ready
  const targetWorker = await findServiceWorkerForBuild(registration, buildId)
  if (!targetWorker) return { buildId, result: 'retry' }
  if (!navigator.onLine) return { buildId, result: 'retry' }

  const routes = getNativeOfflineRequiredRoutes()
  for (const routeBatch of splitIntoBatches(routes, ROUTE_BATCH_SIZE)) {
    const result = await warmRouteShells(routeBatch)
    if (!result.complete) return { buildId, result: 'retry' }
  }

  if (!(await verifyWarmedRouteShells(routes))) {
    return { buildId, result: 'retry' }
  }
  if (!(await writeNativeOfflineReadinessManifest(routes))) {
    return { buildId, result: 'retry' }
  }

  if (targetWorker.isWaiting) {
    try {
      targetWorker.worker.postMessage({
        type: NATIVE_OFFLINE_SKIP_WAITING_MESSAGE_TYPE,
        buildId,
      })
    } catch {
      return { buildId, result: 'retry' }
    }
    return { buildId, result: 'waiting' }
  }

  return {
    buildId,
    result: (await commitActiveNativeOfflineBuild(registration, buildId))
      ? 'ready'
      : 'retry',
  }
}

/** Retries missing route documents without exposing a download control to users. */
export default function NativeOfflineBootstrap() {
  const isNativeApp = useIsNativeApp()
  const pendingActivationBuildIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isNativeApp || typeof window === 'undefined') return
    if (!('serviceWorker' in navigator)) return

    let isCancelled = false
    let retryTimeoutId: number | null = null

    /** Commits the verified build after the waiting worker becomes active. */
    const finalizePendingActivation = async () => {
      const buildId = pendingActivationBuildIdRef.current
      if (!buildId || isCancelled) return

      try {
        const registration = await navigator.serviceWorker.ready
        const committed = await commitActiveNativeOfflineBuild(registration, buildId)
        if (!committed || isCancelled) {
          if (!isCancelled) scheduleRetry()
          return
        }

        pendingActivationBuildIdRef.current = null
        window.setTimeout(() => window.location.reload(), 0)
      } catch {
        scheduleRetry()
      }
    }

    /** Completes the waiting-worker handoff without requiring network access. */
    const handleControllerChange = () => {
      void finalizePendingActivation()
    }

    /** Retries an incomplete shell preparation without adding user controls. */
    const scheduleRetry = () => {
      if (isCancelled || retryTimeoutId !== null) return
      retryTimeoutId = window.setTimeout(() => {
        retryTimeoutId = null
        void attemptPreparation()
      }, RETRY_DELAY_MS)
    }

    /** Prepares the current build or requests another bounded retry. */
    const attemptPreparation = async () => {
      if (isCancelled) return
      if (!navigator.onLine) {
        if (pendingActivationBuildIdRef.current) void finalizePendingActivation()
        return
      }

      try {
        const preparation = await prepareNativeOfflineShell()
        if (preparation.result === 'waiting' && preparation.buildId) {
          pendingActivationBuildIdRef.current = preparation.buildId
          scheduleRetry()
          return
        }
        if (preparation.result !== 'ready') scheduleRetry()
      } catch {
        scheduleRetry()
      }
    }

    /** Restarts preparation when connectivity returns. */
    const handleOnline = () => {
      if (retryTimeoutId !== null) {
        window.clearTimeout(retryTimeoutId)
        retryTimeoutId = null
      }
      void attemptPreparation()
    }

    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange)
    window.addEventListener('online', handleOnline)

    const startPreparation = async () => {
      if (await isNativeOfflineReadyForCurrentBuild()) return
      void attemptPreparation()
    }

    void startPreparation()

    return () => {
      isCancelled = true
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        handleControllerChange,
      )
      window.removeEventListener('online', handleOnline)
      if (retryTimeoutId !== null) window.clearTimeout(retryTimeoutId)
    }
  }, [isNativeApp])

  return null
}
